import { IFC_CLASSES_CEILING_DEVICES } from "./deviceCategoryConfig";
import { RoomSideBarTabs } from "./designEnums";
import { DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS } from "./schemas/viewerView.schema";
import { ROOM_COLORING_IDS } from "./schemas/roomColoring.schema";
import { ACTIVE_MODULE_KEYS } from "./schemas/foundationSchema";
/** Display settings only: applying a preset preserves floor, camera and module. */
export const COMMON_ARCHITECTURAL_IFC_CLASSES = [
    "IfcWall", "IfcWallStandardCase", "IfcDoor", "IfcWindow", "IfcSanitaryTerminal", "IfcFurniture",
];
export const ARCHITECTURAL_BACKGROUND = COMMON_ARCHITECTURAL_IFC_CLASSES.map(ifcClass => ({ ifcClass, opacity: 0.35 }));
export const VENTILATION_IFC_CLASSES = [
    "IfcAirTerminal", "IfcAirTerminalBox", "IfcDuctSegment", "IfcDuctFitting",
    "IfcDamper", "IfcDuctSilencer", "IfcFan", "IfcFilter", "IfcCoil",
    "IfcUnitaryEquipment", "IfcCooledBeam",
];
const furnitureClasses = COMMON_ARCHITECTURAL_IFC_CLASSES.map(ifcClass => ({ ifcClass, opacity: 1 }));
export const MODULE_DEFAULT_VIEW_PRESETS = {
    "ceiling-default": {
        source: "builtIn", key: "ceiling-default", name: "Himling",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler uten romfarging.", modules: ["CEILING"],
        view: { schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: RoomSideBarTabs.CEILING_MULTIPLACER_PRODUCTS, visibility: { mode: "INSTANCE_CLASS", ifcClasses: ARCHITECTURAL_BACKGROUND },
            componentColorMode: "SYSTEM_AND_CLASS", roomColoringId: null },
    },
    "room-validation-default": {
        source: "builtIn", key: "room-validation-default", name: "Romvalidering",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler med samlet forekomstvalidering på rom.", modules: ["ROOM_VALIDATION"],
        view: { schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES, visibility: { mode: "INSTANCE_CLASS", ifcClasses: furnitureClasses },
            componentColorMode: "SYSTEM_AND_CLASS", roomColoringId: "specifiedArticles.overall" },
    },
    "explore-default": {
        source: "builtIn", key: "explore-default", name: "Utforsker",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler uten romfarging.", modules: ["EXPLORE"],
        view: { schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: RoomSideBarTabs.ROOM_INFO, visibility: { mode: "INSTANCE_CLASS", ifcClasses: furnitureClasses },
            componentColorMode: "SYSTEM_AND_CLASS", roomColoringId: null },
    },
    "room-numbering-default": {
        source: "builtIn", key: "room-numbering-default", name: "Romnummerering",
        description: "Viser vegger og dører med romnummereringsfarger på rom.", modules: ["ROOM_NUMBERING"],
        view: { schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: RoomSideBarTabs.ROOM_NUMBERING_DETAILS, visibility: { mode: "INSTANCE_CLASS", ifcClasses: ["IfcWall", "IfcWallStandardCase", "IfcDoor"].map(ifcClass => ({ ifcClass, opacity: 1 })) },
            componentColorMode: "SYSTEM_AND_CLASS", roomColoringId: ROOM_COLORING_IDS.ROOM_NUMBERING },
    },
    "drofus-changes-default": {
        source: "builtIn", key: "drofus-changes-default", name: "Endringshåndtering",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler uten romfarging.", modules: ["DROFUS_CHANGE_MANAGEMENT"],
        view: { schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: RoomSideBarTabs.ROOM_DROFUS_CHANGES, visibility: { mode: "INSTANCE_CLASS", ifcClasses: furnitureClasses },
            componentColorMode: "SYSTEM_AND_CLASS", roomColoringId: null },
    },
};
export const BUILT_IN_VIEW_PRESETS = [
    {
        source: "builtIn",
        key: "furniture-plan",
        name: "Arkitekt",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler.",
        modules: [...ACTIVE_MODULE_KEYS],
        view: {
            schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: null,
            componentColorMode: "SYSTEM_AND_CLASS",
            roomColoringId: null,
            visibility: {
                mode: "INSTANCE_CLASS",
                ifcClasses: furnitureClasses,
            },
        },
    },
    {
        source: "builtIn",
        key: "architect-mmi",
        name: "Arkitekt MMI",
        description: "Arkitektvisning med MMI-farger på komponenter og rom.",
        modules: [...ACTIVE_MODULE_KEYS],
        view: {
            schemaVersion: 1, modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: null,
            visibility: { mode: "INSTANCE_CLASS", ifcClasses: furnitureClasses },
            componentColorMode: "MMI",
            roomColoringId: ROOM_COLORING_IDS.MMI,
        },
    },
    {
        source: "builtIn",
        key: "ceiling-components",
        name: "Himlingskomponenter",
        description: "Viser vegger, dører, vinduer, sanitærutstyr og møbler sammen med luftdon, lysarmaturer, sprinkler, sensorer og kjøleenheter.",
        modules: [...ACTIVE_MODULE_KEYS],
        view: {
            schemaVersion: 1,
            modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: null,
            visibility: {
                mode: "INSTANCE_CLASS",
                ifcClasses: [
                    ...ARCHITECTURAL_BACKGROUND,
                    ...IFC_CLASSES_CEILING_DEVICES.map(ifcClass => ({ ifcClass, opacity: 1 })),
                ],
            },
            componentColorMode: "SYSTEM_AND_CLASS",
            roomColoringId: null,
        },
    },
    {
        source: "builtIn",
        key: "ventilation",
        name: "Ventilation",
        description: "Viser kanaler, kanaldeler, luftdon, spjeld, lyddempere og ventilasjonsutstyr med nedtonet arkitektunderlag.",
        modules: [...ACTIVE_MODULE_KEYS],
        view: {
            schemaVersion: 1,
            modules: { ROOM_VALIDATION: DEFAULT_ROOM_VALIDATION_VIEW_SETTINGS }, articleColoringId: null, roomPanelTab: null,
            visibility: {
                mode: "INSTANCE_CLASS",
                ifcClasses: [
                    ...ARCHITECTURAL_BACKGROUND,
                    ...VENTILATION_IFC_CLASSES.map(ifcClass => ({ ifcClass, opacity: 1 })),
                ],
            },
            componentColorMode: "SYSTEM_AND_CLASS",
            roomColoringId: null,
        },
    },
    ...Object.values(MODULE_DEFAULT_VIEW_PRESETS),
];
